import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, Upload } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Switch from '../../../components/ui/Switch';
import { useAuth } from '../../../contexts/AuthContext';
import { productService } from '../../../services/productService'; // For searching existing products
import { sellerProductService } from '../../../services/sellerProductService'; // For creating seller products
import { sellerService } from '../../../services/sellerService'; // To get seller_id
import { imageUploadService } from '../../../utils/imageUploadService'; // Add imageUploadService
import { supabase } from '../../../lib/supabase';
import { validateVIN } from '../../../utils/validators';

// Define seller product statuses
const SELLER_PRODUCT_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'deleted', label: 'Deleted' },
];

export default function AddProductOfferingModal({ onClose, onProductAdded }) {
  const { user } = useAuth();
  const [sellerId, setSellerId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [formData, setFormData] = useState({
    price: '',
    stockAmount: '',
    purchaseLimit: '',
    stockCode: '',
    status: 'active', // Default status
    condition: 'New', // NEW: Default condition
    description: '',
    isCertified: false,
    staffpick: false,
    vin_sn: '',
    isSellingPhysicalProduct: false,
    sellProductOnly: false, // NEW: Sell product assembly only flag
    vinSn: '',
    specs: '{}',
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [vinError, setVinError] = useState('');
  const [isPre1981, setIsPre1981] = useState(false);
  const [isPre1969, setIsPre1969] = useState(false);
  const searchInputRef = useRef(null);
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Keep focus on search input
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchResults]); // Only re-focus when search results change

  // Fetch seller ID on component mount
  useEffect(() => {
    const fetchSellerId = async () => {
      if (user?.id) {
        try {
          const sellerAccount = await sellerService.getSellerAccountForUser(user.id);
          if (sellerAccount) {
            setSellerId(sellerAccount.id);
          } else {
            setError('Seller account not found for current user.');
          }
        } catch (err) {
          console.error('Failed to fetch seller ID:', err);
          setError('Failed to load seller account.');
        }
      }
    };
    fetchSellerId();
  }, [user?.id]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const validSearchTerm = typeof searchTerm === 'string' ? searchTerm.trim() : '';
      if (!validSearchTerm) {
          setSearchResults([]);
          return;
      }
      const results = await productService.searchProducts(validSearchTerm);
      console.log('Search results in modal:', results);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching products:', err);
      setError('Failed to search for products.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // Handle product selection from search results
  const handleProductSelect = useCallback((product) => {
    setSelectedProductId(product.id);
    setSelectedProductDetails(product);
    // Pre-fill description and sellProductOnly override flag!
    setFormData(prev => ({
      ...prev,
      description: prev.description || product.description || '',
      sellProductOnly: product.sell_product_only || product.sellProductOnly || false,
    }));
    setSearchResults([]); // Clear search results after selection
    setSearchTerm(product.name); // Keep selected product name in search bar
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'vinSn') {
      setVinError('');
    }
  };

  const handleVinBlur = (e) => {
    const val = e.target.value;
    const isVehicle = selectedProductDetails?.categoryName?.toLowerCase().includes('automotive') || selectedProductDetails?.categoryName?.toLowerCase().includes('motorcycles');
    if (isVehicle && val) {
      if (!validateVIN(val, { isPre1981, isPre1969 })) {
        setVinError('Invalid VIN format. Please check the Vehicle Identification Number.');
      } else {
        setVinError('');
      }
    } else {
      setVinError('');
    }
  };

  useEffect(() => {
    // Revalidate if toggles change and we already have a VIN
    if (formData.vinSn) {
        handleVinBlur({ target: { value: formData.vinSn } });
    }
  }, [isPre1981, isPre1969]);
  const handleStatusChange = (value) => {
    setFormData(prev => ({ ...prev, status: value }));
  };

  const handleImageFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Validate files
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    const newFiles = [];
    const newPreviews = [];

    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        setError(`File ${file.name} is not a valid image type (JPG, PNG, WEBP).`);
        return;
      }
      if (file.size > maxSize) {
        setError(`File ${file.name} exceeds the 5MB size limit.`);
        return;
      }
      newFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    setError('');
    setSelectedImageFiles(prev => [...prev, ...newFiles]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    setSelectedImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };
  
  const uploadImages = async () => {
    if (selectedImageFiles.length === 0) return [];

    setUploading(true);
    const uploadPromises = selectedImageFiles.map(async (file) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `seller-product-images/${sellerId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('seller-product-images')
        .upload(filePath, file);

      if (uploadError) {
        throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
      }

      const { data } = supabase.storage.from('seller-product-images').getPublicUrl(filePath);
      return data.publicUrl;
    });

    try {
      const urls = await Promise.all(uploadPromises);
      return urls;
    } catch (error) {
      setError(error.message);
      return [];
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!sellerId) {
      setError('Seller ID not available. Please ensure your seller account is set up.');
      setLoading(false);
      return;
    }
    if (!selectedProductId) {
      setError('Please select a product from the search results.');
      setLoading(false);
      return;
    }
    if (formData.isSellingPhysicalProduct) {
        if (!formData.price || isNaN(parseFloat(formData.price)) || parseFloat(formData.price) < 0) {
            setError('Please enter a valid price for the product.');
            setLoading(false);
            return;
        }
        if (!formData.stockAmount || isNaN(parseInt(formData.stockAmount, 10)) || parseInt(formData.stockAmount, 10) < 0) {
            setError('Please enter a valid stock amount.');
            setLoading(false);
            return;
        }
    }

    const isVehicle = selectedProductDetails?.categoryName?.toLowerCase().includes('automotive') || selectedProductDetails?.categoryName?.toLowerCase().includes('motorcycles');
    if (isVehicle && formData.vinSn) {
        if (!validateVIN(formData.vinSn, { isPre1981, isPre1969 })) {
            setVinError('Invalid VIN format. Please check the Vehicle Identification Number.');
            setLoading(false);
            return;
        }
    }

    let parsedSpecs = {};
    try {
      parsedSpecs = formData.specs && formData.specs.trim() !== '' ? JSON.parse(formData.specs) : {};
    } catch (e) {
      setError('Invalid JSON format for Specifications.');
      setLoading(false);
      return;
    }
    
    if (isPre1981 || isPre1969) {
        parsedSpecs.isPre1981 = isPre1981;
        parsedSpecs.isPre1969 = isPre1969;
    }

    try {
      const uploadedImageUrls = await uploadImages();
      
      const dataToSubmit = {
        seller_id: sellerId,
        product_id: selectedProductId,
        price: formData.isSellingPhysicalProduct ? parseFloat(formData.price) : 0,
        stock_amount: formData.isSellingPhysicalProduct ? parseInt(formData.stockAmount, 10) : 0,
        purchase_limit: formData.purchaseLimit ? parseInt(formData.purchaseLimit, 10) : null,
        stock_code: formData.stockCode,
        status: formData.status,
        condition: formData.condition || 'New',
        description: formData.description,
        seller_image_url: uploadedImageUrls,
        is_certified: formData.isCertified,
        staffpick: formData.staffpick,
        vin_sn: formData.isSellingPhysicalProduct ? formData.vinSn : null,
        specs: parsedSpecs,
        sell_product_only: Boolean(formData.sellProductOnly)
      };
      
      await sellerProductService.createSellerProduct(dataToSubmit);

      onProductAdded();
      onClose();
    } catch (err) {
      console.error('Failed to add seller product:', err);
      setError(err.message || 'Failed to add product to your store.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full h-[90vh] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add Product Offering to Store</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Product Search */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Product (from main catalog) *
            </label>
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setSelectedProductId(null);
                    setSelectedProductDetails(null);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Press Enter to search by product name, SKU, or UPC"
                  className="pl-10 w-full"
                  disabled={loading}
                />
              </div>
              <Button type="button" onClick={handleSearch} disabled={loading}>
                Search
              </Button>
            </div>
            <ul className={`absolute z-20 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1 ${
              (!loading && searchResults?.length > 0 && !selectedProductId) ? '' : 'hidden'
            }`}>
              {searchResults.map(product => (
                <li 
                  key={product.id} 
                  className="p-3 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                  onClick={() => handleProductSelect(product)}
                >
                  {product.thumbnailPath && (
                      <img src={product.thumbnailPath.startsWith('http') ? product.thumbnailPath : imageUploadService.getPublicUrl(product.thumbnailPath, 'product-thumbnails')} alt={product.name} className="w-10 h-10 object-cover rounded-md" />
                  )}
                  <div>
                      <p className="text-sm font-medium text-gray-900">{product.name}</p>
                      <p className="text-xs text-gray-500">SKU: {product.sku}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Product Identifiers */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Product Identifiers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catalog SKU
                </label>
                <p className="text-sm text-gray-500">{selectedProductDetails?.sku || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catalog UPC
                </label>
                <p className="text-sm text-gray-500">{selectedProductDetails?.upc || 'N/A'}</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Stock Code (Optional)
              </label>
              <Input
                type="text"
                name="stockCode"
                value={formData?.stockCode}
                onChange={handleChange}
                placeholder="Enter your stock code"
                disabled={!selectedProductId}
              />
            </div>
          </div>

          {/* Seller-specific Product Details */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Seller-Specific Details</h3>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-md bg-blue-50">
                <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Sell Physical Product</h3>
                    <p className="text-xs text-gray-600">Enable this to list the fully assembled product for sale.</p>
                </div>
                <Switch 
                  checked={formData.isSellingPhysicalProduct} 
                  onCheckedChange={(checked) => setFormData({...formData, isSellingPhysicalProduct: checked})} 
                  disabled={!selectedProductId}
                />
              </div>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-md bg-blue-50">
                <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Sell Product Assembly Only</h3>
                    <p className="text-xs text-gray-600">If enabled, clicking to add/configure any individual component inside the 3D model viewer will instead automatically add this entire parent product assembly to the cart.</p>
                </div>
                <Switch 
                  checked={formData.sellProductOnly} 
                  onCheckedChange={(checked) => setFormData({...formData, sellProductOnly: checked})} 
                  disabled={!selectedProductId}
                />
              </div>

              {formData.isSellingPhysicalProduct && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Price *</label>
                      <Input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        required
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        disabled={!selectedProductId}
                        className="pl-7"
                      />
                      <span className="absolute left-3 top-[34px] text-gray-500 font-medium">$</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Limit (Optional)</label>
                      <Input
                        type="number"
                        name="purchaseLimit"
                        value={formData.purchaseLimit}
                        onChange={handleChange}
                        min="1"
                        placeholder="No limit"
                        disabled={!selectedProductId}
                      />
                      <p className="text-[10px] text-gray-500 mt-0.5">Maximum units a customer can buy per order</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Your Stock Quantity *
                      </label>
                      <Input
                        type="number"
                        name="stockAmount"
                        value={formData?.stockAmount}
                        onChange={handleChange}
                        required
                        min="0"
                        placeholder="0"
                        disabled={!selectedProductId}
                      />
                    </div>
                    {(selectedProductDetails?.categoryName?.toLowerCase().includes('automotive') || selectedProductDetails?.categoryName?.toLowerCase().includes('motorcycles')) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          VIN / Serial Number
                        </label>
                        <Input
                          type="text"
                          name="vinSn"
                          value={formData?.vinSn}
                          onChange={handleChange}
                          onBlur={handleVinBlur}
                          error={vinError}
                          placeholder="Enter VIN or Serial Number"
                          disabled={!selectedProductId}
                        />
                        {(selectedProductDetails?.categoryName?.toLowerCase().includes('automotive') || selectedProductDetails?.categoryName?.toLowerCase().includes('motorcycles')) && (
                          <div className="flex gap-4 mt-2">
                            <label className="flex items-center space-x-2 text-sm text-gray-600">
                              <input type="checkbox" checked={isPre1981} onChange={(e) => setIsPre1981(e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500" />
                              <span>Is year before 1981?</span>
                            </label>
                            <label className="flex items-center space-x-2 text-sm text-gray-600">
                              <input type="checkbox" checked={isPre1969} onChange={(e) => setIsPre1969(e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500" />
                              <span>Is year before 1969?</span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Condition *
                    </label>
                    <Select
                      name="condition"
                      value={formData?.condition}
                      onChange={(val) => setFormData(prev => ({ ...prev, condition: val }))}
                      options={[
                        { value: 'New', label: 'New' },
                        { value: 'Used', label: 'Used' },
                        { value: 'Refurbished', label: 'Refurbished' },
                      ]}
                      disabled={!selectedProductId}
                      required
                    />
                  </div>
                </>
              )}

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-2">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Product Badges</h3>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 font-medium w-32">Global Badges:</span>
                      {selectedProductDetails?.isCertified && (
                          <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-md">FACTORY CERTIFIED</span>
                      )}
                      {selectedProductDetails?.staffpick && (
                          <span className="px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded-md">GLOBAL STAFF PICK</span>
                      )}
                      {(!selectedProductDetails || (!selectedProductDetails.isCertified && !selectedProductDetails.staffpick)) && (
                          <span className="text-sm text-gray-400 italic">No global badges assigned to this product</span>
                      )}
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600 font-medium w-32">Store Badges:</span>
                      <div className="flex items-center">
                        <Switch 
                          checked={formData.staffpick} 
                          onCheckedChange={(checked) => setFormData({...formData, staffpick: checked})} 
                          disabled={!selectedProductId}
                        />
                        <label className="ml-2 text-sm text-gray-700">Store Pick (Displays only in your store)</label>
                      </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <Select
                  name="status"
                  value={formData?.status}
                  onChange={handleStatusChange}
                  options={SELLER_PRODUCT_STATUSES}
                  placeholder="Select status"
                  disabled={!selectedProductId}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Product Description (Optional, overrides main product description)
                </label>
                <textarea
                  name="description"
                  value={formData?.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your specific product description"
                  disabled={!selectedProductId}
                />
              </div>

              {/* Seller Image Uploader */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Product Images (Optional, overrides main product images)
                </label>
                <div className="mt-2">
                  <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {selectedImageFiles.length > 0 ? `${selectedImageFiles.length} file(s) selected` : 'Choose files (JPG, PNG, WEBP - Max 5MB)'}
                    </span>
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageFileSelect}
                      className="hidden"
                      disabled={!selectedProductId || uploading}
                    />
                  </label>
                </div>
                {imagePreviews.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative aspect-square">
                        <img src={preview} alt={`Preview ${index}`} className="w-full h-full object-cover rounded-md" />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm mt-4">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading || uploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || uploading || !selectedProductId || !!vinError}>
              {uploading ? 'Uploading...' : loading ? 'Adding...' : 'Add Product to Store'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}